import React, {useMemo, useState} from 'react';
import {useWatch} from 'react-hook-form';
import type {CourseVideoRequest} from 'src/api/ProactApiClient';
import {EntityIdentifierEnum, ModuleEnum} from 'src/api/ProactApiClient';
import {
  useCreateCourseVideoMutation,
  useGetCourseVideoQuery,
  useUpdateCourseVideoMutation,
} from 'src/redux/queries/courseVideo';
import {SaveButton} from 'src/design/molecules/SaveButton';
import {BackBar} from 'src/app/layout/BackBar';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useEntitySyncedForm} from 'src/app/forms/engine/useEntitySyncedForm';
import {FormViewComponentV2} from 'src/app/forms/FormViewComponent/FormViewComponentV2';
import {SidebarLinkTraining} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {useAuditLogPermissions} from 'src/shared/auth/permissions/useAuditLogPermissions';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import type {TrainingBreadcrumbMode} from 'src/shared/hooks/useTrainingBreadcrumbs';
import {useTrainingBreadcrumbs} from 'src/shared/hooks/useTrainingBreadcrumbs';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {AuditModule} from 'src/app/audit-logs/AuditModule';
import {
  courseVideoDocumentTitle,
  CourseVideoFormMeta,
  GetDefaultCourseVideoFormData,
  MapCourseVideoResponseToFormData,
} from './courseVideoHelpers';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export type CourseVideoViewType = 'content' | 'audit' | '' | ':view';

export interface CourseVideoFormProps {
  id?: number;
  view?: string;
  mode: 'create' | 'view' | 'edit';
}

export const CourseVideoForm: React.FC<CourseVideoFormProps> = ({
  id,
  view,
  mode,
}) => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();
  const [isSavePending, setIsSavePending] = useState(false);
  useSetDocumentTitle(courseVideoDocumentTitle);

  const trainingPerms = useModulePermissions(ModuleEnum.Training);
  const auditPerms = useAuditLogPermissions();

  const formMeta = useMemo(() => CourseVideoFormMeta(), []);

  const [createCourseVideo] = useCreateCourseVideoMutation();
  const [updateCourseVideo] = useUpdateCourseVideoMutation();

  const {data: videoData, isLoading} = useGetCourseVideoQuery(id!, {
    skip: id === undefined,
  });

  const form = useEntitySyncedForm({
    formMeta,
    initialData: GetDefaultCourseVideoFormData(),
    enablePreSubmissionValidation: false,
    entity: videoData,
    toFormData: MapCourseVideoResponseToFormData,
  });

  const handleSave = async () => {
    const isValid = await form.validateForm();
    if (!isValid) {
      return;
    }

    try {
      setIsSavePending(true);

      const body = {
        ...videoData,
        ...form.getFieldValues(),
      } as CourseVideoRequest;

      const res =
        id === undefined
          ? await createCourseVideo(body).unwrap()
          : await updateCourseVideo(body).unwrap();

      setAlert('Item was successfully saved', 'success');

      navigate(PathUtility.CourseVideoView(String(res.id), 'content'), {
        replace: true,
      });
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    } finally {
      setIsSavePending(false);
    }
  };

  const handleEdit = () => navigate(PathUtility.CourseVideoEdit(+id!));

  const getBreadcrumbMode = (): TrainingBreadcrumbMode => {
    switch (mode) {
      case 'create':
        return 'create-course-video';
      case 'edit':
        return 'edit-course-video';
      default:
        return 'course-video';
    }
  };

  const watchedTitle = useWatch({control: form.methods.control, name: 'title'});
  const title =
    mode === 'view' ? videoData?.title : (watchedTitle as string | undefined);

  const {breadcrumbs, fallbackTo, fallbackLabel} = useTrainingBreadcrumbs({
    mode: getBreadcrumbMode(),
    videoId: id,
    videoTitle: title || undefined,
  });

  const {RenderedTabsComponent} = useUrlQueryTabV2<CourseVideoViewType>({
    view: (view ?? 'content') as CourseVideoViewType,
    defaultView: 'content',
    tabs: [
      {
        view: 'content',
        label: 'Content',
        component: (
          <>
            {mode !== 'view' ? (
              <>
                <ProactForm form={form}>
                  <ProactFormGrid />
                </ProactForm>
                <SaveButton loading={isSavePending} onClick={handleSave} />
              </>
            ) : (
              <>
                <FormViewComponentV2
                  formData={
                    videoData ? MapCourseVideoResponseToFormData(videoData) : {}
                  }
                  formMeta={formMeta}
                />
              </>
            )}
          </>
        ),
      },
      {
        view: 'audit',
        label: 'Audit',
        disabled: !auditPerms.view || mode !== 'view',
        component: id && (
          <AuditModule
            entityId={String(id)}
            entityType={EntityIdentifierEnum.CourseVideo}
          />
        ),
      },
    ],
    onSelectView: view =>
      navigate(
        PathUtility.CourseVideoView(String(id), view as CourseVideoViewType),
        {replace: true}
      ),
  });

  return !isLoading ? (
    <>
      <BackBar
        fallbackTo={fallbackTo}
        fallbackLabel={fallbackLabel}
        breadcrumbs={breadcrumbs}
      />
      <PageHeader>
        <PageHeader.Title icon={SidebarLinkTraining.icon}>
          {title || 'Course Video'}
        </PageHeader.Title>
        {mode === 'edit' && (
          <PageHeader.Badges>
            <PageHeader.Badge color="yellow" variant="light">
              Editing
            </PageHeader.Badge>
          </PageHeader.Badges>
        )}
        <PageHeader.Actions>
          {mode === 'view' && trainingPerms.update && (
            <PageHeader.Action variant="edit" onClick={handleEdit}>
              Edit
            </PageHeader.Action>
          )}
          {mode !== 'view' && (
            <PageHeader.Action
              variant="save"
              onClick={handleSave}
              loading={isSavePending}>
              Save
            </PageHeader.Action>
          )}
        </PageHeader.Actions>
      </PageHeader>
      {RenderedTabsComponent}
    </>
  ) : (
    <TableBodyRender isLoading={isLoading} hasData={videoData !== undefined} />
  );
};
